import { Box, Typography, Container, Paper, Chip, Autocomplete, TextField, Skeleton, IconButton, Popover, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert } from "@mui/material";
import styles from "./style.module.css";
import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import InsightsIcon from '@mui/icons-material/Insights';

import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { getQuickSearches, saveQuickSearch, deleteQuickSearch } from "../../services/business/dashboad";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function DashboardComponent(props) {
  const {
    v4Data, selectedLocationIds, onLocationChange, isLoading,
  } = props;

  // ── v4 Dashboard Data ──
  const kpi = v4Data?.kpi;
  const userLocations = v4Data?.userLocations || [];
  const userId = v4Data?.userId;
  const REVENUE_ALLOWED_IDS = [8, 9, 43];
  const canSeeRevenue = REVENUE_ALLOWED_IDS.includes(userId);
  const reservedByCategory = v4Data?.reservedByCategory || {};
  const categoryAnalytics = v4Data?.categoryAnalytics || [];
  const categoryCancelled = v4Data?.categoryCancelled || [];
  const categoryInquiries = v4Data?.categoryInquiries || [];

  // ── Filters for Reserved by Category ──
  const [periodFilter, setPeriodFilter] = useState('monthly');
  const [statusFilter, setStatusFilter] = useState('all');

  const PERIOD_OPTIONS = [
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
  ];
  const STATUS_OPTIONS = [
    { key: 'all', label: 'All' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'noshow', label: 'No Show' },
  ];

  // ── Quick Searches (saved location presets, persisted server-side) ──
  const [quickSearches, setQuickSearches] = useState([]);
  const [saveAnchorEl, setSaveAnchorEl] = useState(null);
  const [newPresetName, setNewPresetName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    getQuickSearches().then((res) => {
      if (res?.APIResult === 1 && res.quickSearches) {
        setQuickSearches(res.quickSearches);
      }
    });
  }, []);

  const handleSavePreset = async () => {
    const name = newPresetName.trim();
    if (!name || !selectedLocationIds?.length) return;
    const result = await saveQuickSearch(name, selectedLocationIds);
    if (result?.APIResult === 1 && result.id) {
      setQuickSearches((prev) => [
        ...prev.filter((q) => q.name !== name),
        { id: result.id, name: result.name, locationIds: result.locationIds },
      ]);
    }
    setNewPresetName('');
    setSaveAnchorEl(null);
  };

  const handleDeletePreset = async () => {
    const qs = deleteConfirm;
    if (!qs) return;
    setDeleteConfirm(null);
    const prev = [...quickSearches];
    setQuickSearches((s) => s.filter((q) => q.id !== qs.id));
    const result = await deleteQuickSearch(qs.id);
    if (result?.APIResult !== 1 && result?.status !== "SUCCESS") {
      setQuickSearches(prev);
    } else {
      setSnackbar({ open: true, message: `Deleted quick search filter "${qs.name}"` });
    }
  };

  const handleApplyPreset = (locationIds) => {
    onLocationChange?.(locationIds);
  };

  // ── Bar Chart: Reserved by Category ──
  const barChartOptions = useMemo(() => {
    if (statusFilter === 'all') {
      // Show 3 grouped bars: confirmed (green), cancelled (red), no show (yellow)
      const confirmedData = reservedByCategory.confirmed || [];
      const cancelledData = reservedByCategory.cancelled || [];
      const noshowData = reservedByCategory.noshow || [];
      // Merge all category titles across all statuses
      const allCats = [...new Set([
        ...confirmedData.map(r => r.categoryTitle),
        ...cancelledData.map(r => r.categoryTitle),
        ...noshowData.map(r => r.categoryTitle),
      ])];
      if (!allCats.length) return null;
      const getValue = (dataset, cat) => dataset.find(r => r.categoryTitle === cat)?.[periodFilter] || 0;
      return {
        options: {
          chart: { type: 'bar', fontFamily: 'Inter, sans-serif', toolbar: { show: false } },
          plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 6 } },
          dataLabels: { enabled: false },
          xaxis: { categories: allCats, labels: { style: { fontSize: '12px', colors: '#64748b' } } },
          yaxis: { labels: { style: { fontSize: '12px', colors: '#64748b' } } },
          colors: ['#10b981', '#ef4444', '#f59e0b'],
          legend: { position: 'top', fontSize: '13px', fontWeight: 600 },
          grid: { borderColor: '#f1f5f9' },
          tooltip: { theme: 'light' },
        },
        series: [
          { name: 'Confirmed', data: allCats.map(c => getValue(confirmedData, c)) },
          { name: 'Cancelled', data: allCats.map(c => getValue(cancelledData, c)) },
          { name: 'No Show', data: allCats.map(c => getValue(noshowData, c)) },
        ],
      };
    }
    // Single status filter
    const data = reservedByCategory[statusFilter] || [];
    if (!data.length) return null;
    const categories = data.map(r => r.categoryTitle);
    const values = data.map(r => r[periodFilter] || 0);
    const colorMap = { confirmed: '#10b981', cancelled: '#ef4444', noshow: '#f59e0b' };
    return {
      options: {
        chart: { type: 'bar', fontFamily: 'Inter, sans-serif', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        xaxis: { categories, labels: { style: { fontSize: '12px', colors: '#64748b' } } },
        yaxis: { labels: { style: { fontSize: '12px', colors: '#64748b' } } },
        colors: [colorMap[statusFilter]],
        legend: { show: false },
        grid: { borderColor: '#f1f5f9' },
        tooltip: { theme: 'light' },
      },
      series: [
        { name: STATUS_OPTIONS.find(s => s.key === statusFilter)?.label, data: values },
      ],
    };
  }, [reservedByCategory, periodFilter, statusFilter]);

  // ── Line Chart: Category Analytics (last 6 months) ──
  const lineChartOptions = useMemo(() => {
    if (!categoryAnalytics.length) return null;
    const cats = [...new Set(categoryAnalytics.map(c => c.categoryTitle))];
    // month is now "YYYY-MM" (e.g. "2025-11"), sort chronologically
    const months = [...new Set(categoryAnalytics.map(c => c.month))].sort();
    const monthLabels = months.map(m => {
      const [y, mo] = m.split('-');
      return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${y}`;
    });
    const COLORS = ['#3c5a98', '#f59e0b', '#10b981'];
    return {
      options: {
        chart: { type: 'line', fontFamily: 'Inter, sans-serif', toolbar: { show: false }, zoom: { enabled: false } },
        xaxis: { categories: monthLabels, labels: { style: { fontSize: '12px', colors: '#64748b' } } },
        yaxis: { labels: { style: { fontSize: '12px', colors: '#64748b' } } },
        colors: COLORS.slice(0, cats.length),
        stroke: { width: 3, curve: 'smooth' },
        markers: { size: 5, strokeWidth: 2, strokeColors: '#fff' },
        legend: { position: 'top', fontSize: '13px', fontWeight: 600 },
        grid: { borderColor: '#f1f5f9' },
        tooltip: { theme: 'light' },
      },
      series: cats.map(cat => ({
        name: cat,
        data: months.map(m => {
          const match = categoryAnalytics.find(c => c.categoryTitle === cat && c.month === m);
          return match?.count || 0;
        }),
      })),
    };
  }, [categoryAnalytics]);

  // ── Line Chart: Category Analytics — Top 3 Cancelled (last 6 months) ──
  const cancelledChartOptions = useMemo(() => {
    if (!categoryCancelled.length) return null;
    const cats = [...new Set(categoryCancelled.map(c => c.categoryTitle))];
    const months = [...new Set(categoryCancelled.map(c => c.month))].sort();
    const monthLabels = months.map(m => {
      const [y, mo] = m.split('-');
      return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${y}`;
    });
    const COLORS = ['#ef4444', '#f59e0b', '#64748b'];
    return {
      options: {
        chart: { type: 'line', fontFamily: 'Inter, sans-serif', toolbar: { show: false }, zoom: { enabled: false } },
        xaxis: { categories: monthLabels, labels: { style: { fontSize: '12px', colors: '#64748b' } } },
        yaxis: { labels: { style: { fontSize: '12px', colors: '#64748b' } } },
        colors: COLORS.slice(0, cats.length),
        stroke: { width: 3, curve: 'smooth' },
        markers: { size: 5, strokeWidth: 2, strokeColors: '#fff' },
        legend: { position: 'top', fontSize: '13px', fontWeight: 600 },
        grid: { borderColor: '#f1f5f9' },
        tooltip: { theme: 'light' },
      },
      series: cats.map(cat => ({
        name: cat,
        data: months.map(m => {
          const match = categoryCancelled.find(c => c.categoryTitle === cat && c.month === m);
          return match?.count || 0;
        }),
      })),
    };
  }, [categoryCancelled]);

  // ── Bar Chart: Category Inquiries (last 6 months) ──
  const inquiryChartOptions = useMemo(() => {
    if (!categoryInquiries.length) return null;
    // Build all 6 months (current month back 5) so x-axis always has 6 labels
    const now = new Date();
    const allMonths = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      allMonths.push(`${yyyy}-${mm}`);
    }
    const monthLabels = allMonths.map(m => {
      const [y, mo] = m.split('-');
      return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${y}`;
    });
    const allCats = [...new Set(categoryInquiries.map(c => c.categoryTitle))];
    const COLORS = ['#3c5a98', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];
    return {
      options: {
        chart: { type: 'bar', fontFamily: 'Inter, sans-serif', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: monthLabels,
          labels: { style: { fontSize: '12px', colors: '#64748b' } },
        },
        yaxis: { labels: { style: { fontSize: '12px', colors: '#64748b' } } },
        colors: COLORS.slice(0, allCats.length),
        legend: { position: 'top', fontSize: '13px', fontWeight: 600 },
        grid: { borderColor: '#f1f5f9' },
        tooltip: { theme: 'light' },
      },
      series: allCats.map(cat => ({
        name: cat,
        data: allMonths.map(m => {
          const match = categoryInquiries.find(c => c.categoryTitle === cat && c.month === m);
          return match?.inquiries || 0;
        }),
      })),
    };
  }, [categoryInquiries]);

  // ── v4 KPI Cards ──
  const v4KpiCards = kpi ? [
    { icon: <FlightTakeoffIcon sx={{ fontSize: 20 }} />, label: "Pick Ups Today", value: kpi.pickupsToday, color: "#3c5a98" },
    { icon: <FlightLandIcon sx={{ fontSize: 20 }} />, label: "Drop Offs Today", value: kpi.dropoffsToday, color: "#10b981" },
    { icon: <EventBusyIcon sx={{ fontSize: 20 }} />, label: "No Shows Today", value: kpi.noShowsToday, color: "#ef4444" },
    { icon: <BookOnlineIcon sx={{ fontSize: 20 }} />, label: "Booked Today", value: kpi.bookedToday, color: "#8b5cf6" },
    { icon: <CancelIcon sx={{ fontSize: 20 }} />, label: "Cancelled Today", value: kpi.cancelledToday, color: "#f59e0b" },
    { icon: <BlockIcon sx={{ fontSize: 20 }} />, label: "Blackouts Today", value: kpi.blackoutsToday, color: "#64748b" },
    ...(canSeeRevenue ? [
      { icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />, label: "Revenue (Drop Offs)", value: `$${(kpi.revenueByDropoffsToday || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, color: "#10b981" },
      { icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />, label: "Revenue (Booked)", value: `$${(kpi.revenueByBookedToday || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, color: "#3c5a98" },
    ] : []),
  ] : [];

  return (
    <Container maxWidth="lg" sx={{ mt: 3, pb: 4 }} className={styles.dashboardContainer}>
      {isLoading ? (
        <>
          {/* Skeleton: KPI Cards */}
          <Skeleton variant="text" width={180} height={28} sx={{ mb: 1, borderRadius: 1 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            {[...Array(8)].map((_, i) => (
              <Paper key={i} elevation={0} className={styles.cbox} sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="text" width="80%" height={14} />
                </Box>
              </Paper>
            ))}
          </Box>

          {/* Skeleton: Line Charts */}
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 1, borderRadius: 1 }} />
          <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
            {[0, 1].map(i => (
              <Paper key={i} elevation={0} className={styles.cbox} sx={{ p: 3, flex: '1 1 0', minWidth: { xs: '100%', sm: 350 } }}>
                <Skeleton variant="text" width={120} height={22} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={260} sx={{ borderRadius: 2 }} />
              </Paper>
            ))}
          </Box>

          {/* Skeleton: Bar Chart */}
          <Skeleton variant="text" width={220} height={28} sx={{ mt: 4, mb: 1, borderRadius: 1 }} />
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
            {[...Array(7)].map((_, i) => (
              <Skeleton key={i} variant="rounded" width={70} height={26} sx={{ borderRadius: '16px' }} />
            ))}
          </Box>
          <Paper elevation={0} className={styles.cbox} sx={{ p: 3 }}>
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
          </Paper>
        </>
      ) : (
      <>
      {/* ── Location Filter ── */}
      {userLocations.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box className={styles.sectionHeader}>
            <Typography className={styles.sectionTitle}>
              <LocationOnIcon sx={{ fontSize: 18 }} /> Filter by Location
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              multiple
              options={userLocations}
              getOptionLabel={(option) => option.title}
              value={userLocations.filter(l => selectedLocationIds?.includes(l.id))}
              onChange={(_, newVal) => onLocationChange?.(newVal.map(v => v.id))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} placeholder="All locations (select to filter)" size="small" />
              )}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { borderRadius: '12px', background: '#fff' },
              }}
            />
            <IconButton
              disabled={!selectedLocationIds?.length}
              onClick={(e) => setSaveAnchorEl(e.currentTarget)}
              sx={{
                mt: '2px',
                background: selectedLocationIds?.length ? 'rgba(60,90,152,0.1)' : undefined,
                '&:hover': { background: 'rgba(60,90,152,0.18)' },
              }}
              title="Save as Quick Search"
            >
              <SaveIcon sx={{ fontSize: 20, color: selectedLocationIds?.length ? '#3c5a98' : '#cbd5e1' }} />
            </IconButton>
            <Popover
              open={Boolean(saveAnchorEl)}
              anchorEl={saveAnchorEl}
              onClose={() => { setSaveAnchorEl(null); setNewPresetName(''); }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center', minWidth: 260 }}>
                <TextField
                  size="small"
                  placeholder="Preset name"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                  autoFocus
                  inputProps={{ maxLength: 100 }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!newPresetName.trim()}
                  onClick={handleSavePreset}
                  sx={{ textTransform: 'none', background: '#3c5a98', '&:hover': { background: '#2d4373' } }}
                >
                  Save
                </Button>
              </Box>
            </Popover>
          </Box>

          {/* ── Saved Quick Searches ── */}
          {quickSearches.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5, alignItems: 'center' }}>
              <BookmarkIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mr: 0.5 }}>
                Quick Search:
              </Typography>
              {quickSearches.map((qs) => {
                const isActive = selectedLocationIds?.length === qs.locationIds.length &&
                  qs.locationIds.every(id => selectedLocationIds.includes(id));
                return (
                  <Chip
                    key={qs.id}
                    label={`${qs.name} (${qs.locationIds.length})`}
                    size="small"
                    onClick={() => handleApplyPreset(qs.locationIds)}
                    onDelete={() => setDeleteConfirm(qs)}
                    sx={{
                      fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                      ...(isActive
                        ? { background: '#3c5a98', color: '#fff', border: '1px solid #3c5a98',
                            '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } } }
                        : { background: 'rgba(60,90,152,0.08)', color: '#3c5a98', border: '1px solid rgba(60,90,152,0.15)',
                            '& .MuiChip-deleteIcon': { color: '#94a3b8', '&:hover': { color: '#ef4444' } } }),
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ── Delete Quick Search Confirmation ── */}
      <Dialog
        open={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
      >
        <DialogTitle>Delete Quick Search</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to delete the quick search filter{deleteConfirm ? ` "${deleteConfirm.name}"` : ''}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleDeletePreset} color="error" variant="contained" sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── v4 KPI Cards ── */}
      {v4KpiCards.length > 0 && (
        <>
          <Box className={styles.sectionHeader}>
            <Typography className={styles.sectionTitle}>
              <InsightsIcon sx={{ fontSize: 18 }} /> Today's Overview
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            {v4KpiCards.map((card) => (
              <Paper key={card.label} elevation={0} className={styles.cbox} sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: `${card.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color }}>
                  {card.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                    {card.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {card.label}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </>
      )}

      {/* ── Line Charts: Booked & Cancelled side by side ── */}
      {(lineChartOptions || cancelledChartOptions) && (
        <>
          <Box className={styles.sectionHeader} sx={{ mt: 4 }}>
            <Typography className={styles.sectionTitle}>
              <ShowChartIcon sx={{ fontSize: 18 }} /> Category Analytics
            </Typography>
            <Chip label="Last 6 Months" size="small" sx={{ background: 'rgba(60,90,152,0.1)', color: '#3c5a98', fontWeight: 600, fontSize: '0.75rem', border: '1px solid rgba(60,90,152,0.15)' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
            {lineChartOptions && (
              <Paper elevation={0} className={styles.cbox} sx={{ p: 3, flex: '1 1 0', minWidth: { xs: '100%', sm: 350 } }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', mb: 2 }}>
                  Top 3 Booked
                </Typography>
                <Chart options={lineChartOptions.options} series={lineChartOptions.series} type="line" height={300} />
              </Paper>
            )}
            {cancelledChartOptions && (
              <Paper elevation={0} className={styles.cbox} sx={{ p: 3, flex: '1 1 0', minWidth: { xs: '100%', sm: 350 } }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#ef4444', mb: 2 }}>
                  Top 3 Cancelled
                </Typography>
                <Chart options={cancelledChartOptions.options} series={cancelledChartOptions.series} type="line" height={300} />
              </Paper>
            )}
          </Box>
        </>
      )}

      {/* ── Bar Chart: Reserved by Category ── */}
      <>
        <Box className={styles.sectionHeader} sx={{ mt: 4 }}>
          <Typography className={styles.sectionTitle}>
            <BarChartIcon sx={{ fontSize: 18 }} /> Reserved by Category
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mr: 0.5 }}>Period:</Typography>
          {PERIOD_OPTIONS.map(opt => (
            <Chip
              key={opt.key}
              label={opt.label}
              size="small"
              onClick={() => setPeriodFilter(opt.key)}
              sx={{
                fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                ...(periodFilter === opt.key
                  ? { background: '#3c5a98', color: '#fff', border: '1px solid #3c5a98' }
                  : { background: 'rgba(60,90,152,0.08)', color: '#3c5a98', border: '1px solid rgba(60,90,152,0.15)' }),
              }}
            />
          ))}
          <Box sx={{ width: '1px', height: 20, background: '#e2e8f0', mx: 0.5 }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mr: 0.5 }}>Status:</Typography>
          {STATUS_OPTIONS.map(opt => {
            const colorMap = { all: '#3c5a98', confirmed: '#10b981', cancelled: '#ef4444', noshow: '#f59e0b' };
            const c = colorMap[opt.key];
            return (
              <Chip
                key={opt.key}
                label={opt.label}
                size="small"
                onClick={() => setStatusFilter(opt.key)}
                sx={{
                  fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                  ...(statusFilter === opt.key
                    ? { background: c, color: '#fff', border: `1px solid ${c}` }
                    : { background: `${c}14`, color: c, border: `1px solid ${c}30` }),
                }}
              />
            );
          })}
        </Box>
        {barChartOptions ? (
          <Paper elevation={0} className={styles.cbox} sx={{ p: 3, mt: 2 }}>
            <Chart options={barChartOptions.options} series={barChartOptions.series} type="bar" height={350} />
          </Paper>
        ) : (
          <Paper elevation={0} className={styles.cbox} sx={{ p: 3, mt: 2, textAlign: 'center' }}>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No data for selected filters</Typography>
          </Paper>
        )}
      </>

      {/* ── Category Inquiries: Grouped Bar Chart (hidden for now) ── */}
      {/* {inquiryChartOptions && (
        <>
          <Box className={styles.sectionHeader} sx={{ mt: 4 }}>
            <Typography className={styles.sectionTitle}>
              <DonutSmallIcon sx={{ fontSize: 18 }} /> Most Inquired Categories
            </Typography>
            <Chip label="Last 6 Months" size="small" sx={{ background: 'rgba(60,90,152,0.1)', color: '#3c5a98', fontWeight: 600, fontSize: '0.75rem', border: '1px solid rgba(60,90,152,0.15)' }} />
          </Box>
          <Paper elevation={0} className={styles.cbox} sx={{ p: 3, mt: 2 }}>
            <Chart options={inquiryChartOptions.options} series={inquiryChartOptions.series} type="bar" height={350} />
          </Paper>
        </>
      )} */}

      </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ open: false, message: '' })} severity="success" variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
